// src/ui/screens/post/widgets/DesktopCategorySidebar.jsx
//
// Desktop-only permanent category sidebar — the JIJI-style vertical list
// (icon + label + chevron, expanding to subcategories) that replaces the
// mobile SubCategoryModal grid on large screens. Hidden below `lg` via
// `hidden lg:flex`, so mobile is completely unaffected.

import React, { useState } from 'react';
import { propertyCategories } from '../../../../utils/propertyCategories';

import saleIcon from '../../../../assets/svg/sale.svg';
import rentIcon from '../../../../assets/svg/rent.svg';
import shortStayIcon from '../../../../assets/svg/short_stay.svg';
import commercialIcon from '../../../../assets/svg/commercial.svg';
import landIcon from '../../../../assets/svg/land.svg';
import developmentsIcon from '../../../../assets/svg/developments.svg';
import tourismIcon from '../../../../assets/svg/tourism.svg';
import servicesIcon from '../../../../assets/svg/services.svg';
import manufacturersIcon from '../../../../assets/svg/manufacturers.svg';
import suppliersIcon from '../../../../assets/svg/suppliers.svg';

const CATEGORY_SVG_PATHS = [
  saleIcon,
  rentIcon,
  shortStayIcon,
  commercialIcon,
  landIcon,
  developmentsIcon,
  tourismIcon,
  servicesIcon,
  manufacturersIcon,
  suppliersIcon,
];

const ORANGE = '#FF8C08';
const TEAL = '#15B4C4';

const isSpecialCategory = (title) => title === 'Manufacturers' || title === 'Suppliers';

export const DesktopCategorySidebar = ({
  activeSubCategory,
  onSubCategorySelected,
  onClearFilter,
  railWidth = 76,
  width = 264,
}) => {
  const [expandedIndex, setExpandedIndex] = useState(null);

  const toggleExpand = (index) => {
    setExpandedIndex((prev) => (prev === index ? null : index));
  };

  return (
    <div
      className="hidden lg:flex desktop-category-sidebar-scroll"
      style={{
        position: 'fixed',
        left: railWidth,
        top: 0,
        bottom: 0,
        width,
        flexDirection: 'column',
        backgroundColor: '#0F1013',
        borderRight: '1px solid #1C1E23',
        overflowY: 'auto',
        zIndex: 50,
        scrollbarWidth: 'thin',
        scrollbarColor: 'rgba(255,255,255,0.18) transparent',
      }}
    >
      <style>{`
        .desktop-category-sidebar-scroll::-webkit-scrollbar {
          width: 8px;
        }
        .desktop-category-sidebar-scroll::-webkit-scrollbar-track {
          background: transparent;
        }
        .desktop-category-sidebar-scroll::-webkit-scrollbar-thumb {
          background-color: rgba(255, 255, 255, 0.18);
          border-radius: 8px;
          border: 2px solid #0F1013;
        }
        .desktop-category-sidebar-scroll::-webkit-scrollbar-thumb:hover {
          background-color: rgba(255, 255, 255, 0.30);
        }
      `}</style>

      <div style={{ padding: '22px 18px 10px' }}>
        <h1 style={{ color: '#FFFFFF', fontSize: 18, fontWeight: 800, letterSpacing: '-0.01em' }}>
          Filatom
        </h1>
        <p style={{ color: 'rgba(255,255,255,0.35)', fontSize: 12, marginTop: 2 }}>
          Browse by category
        </p>
      </div>

      <button
        onClick={onClearFilter}
        style={{
          margin: '4px 12px 8px',
          padding: '9px 12px',
          borderRadius: 10,
          textAlign: 'left',
          fontSize: 13,
          fontWeight: 600,
          color: !activeSubCategory ? '#FFFFFF' : 'rgba(255,255,255,0.5)',
          backgroundColor: !activeSubCategory ? 'rgba(255,140,8,0.14)' : 'transparent',
          border: 'none',
          cursor: 'pointer',
        }}
      >
        All listings
      </button>

      <div style={{ padding: '0 8px 24px', flex: 1 }}>
        {propertyCategories.map((category, index) => {
          const svgPath = index < CATEGORY_SVG_PATHS.length ? CATEGORY_SVG_PATHS[index] : null;
          const special = isSpecialCategory(category.title);
          const accent = special ? TEAL : ORANGE;
          const isExpanded = expandedIndex === index;
          const hasActiveChild = category.subCategories?.includes(activeSubCategory);

          return (
            <div key={category.title} style={{ marginBottom: 2 }}>
              <button
                onClick={() => toggleExpand(index)}
                style={{
                  width: '100%',
                  display: 'flex',
                  alignItems: 'center',
                  gap: 10,
                  padding: '9px 10px',
                  borderRadius: 10,
                  border: 'none',
                  cursor: 'pointer',
                  backgroundColor: hasActiveChild ? 'rgba(255,140,8,0.10)' : 'transparent',
                }}
                onMouseEnter={(e) => {
                  if (!hasActiveChild) e.currentTarget.style.backgroundColor = 'rgba(255,255,255,0.04)';
                }}
                onMouseLeave={(e) => {
                  if (!hasActiveChild) e.currentTarget.style.backgroundColor = 'transparent';
                }}
              >
                <div
                  style={{
                    width: 28,
                    height: 28,
                    borderRadius: 8,
                    flexShrink: 0,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    backgroundColor: `${accent}1F`,
                  }}
                >
                  {svgPath && (
                    <img
                      src={svgPath}
                      alt=""
                      style={{
                        width: 15,
                        height: 15,
                        filter: special
                          ? 'invert(58%) sepia(59%) saturate(1074%) hue-rotate(133deg) brightness(94%) contrast(92%)'
                          : 'invert(58%) sepia(82%) saturate(2000%) hue-rotate(0deg) brightness(100%) contrast(101%)',
                      }}
                    />
                  )}
                </div>
                <span
                  style={{
                    flex: 1,
                    textAlign: 'left',
                    fontSize: 13,
                    fontWeight: hasActiveChild ? 700 : 500,
                    color: hasActiveChild ? '#FFFFFF' : 'rgba(255,255,255,0.75)',
                  }}
                >
                  {category.title}
                </span>
                <svg
                  width="14"
                  height="14"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="rgba(255,255,255,0.35)"
                  strokeWidth="2"
                  style={{
                    transform: isExpanded ? 'rotate(90deg)' : 'none',
                    transition: 'transform 150ms ease',
                  }}
                >
                  <path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" />
                </svg>
              </button>

              {isExpanded && (
                <div style={{ paddingLeft: 46, paddingRight: 6, marginTop: 2, marginBottom: 6 }}>
                  {category.subCategories.map((sub) => {
                    const isActive = activeSubCategory === sub;
                    return (
                      <button
                        key={sub}
                        onClick={() => onSubCategorySelected(sub)}
                        style={{
                          display: 'block',
                          width: '100%',
                          textAlign: 'left',
                          padding: '6px 8px',
                          borderRadius: 8,
                          fontSize: 12.5,
                          fontWeight: isActive ? 700 : 400,
                          color: isActive ? accent : 'rgba(255,255,255,0.55)',
                          backgroundColor: isActive ? `${accent}14` : 'transparent',
                          border: 'none',
                          cursor: 'pointer',
                        }}
                      >
                        {sub}
                      </button>
                    );
                  })}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default DesktopCategorySidebar;